import React, { FC } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Layout, Menu, Badge, MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  StatsChartIcon as DashboardIcon,
  SwapHorizontalIcon as TransactionsIcon,
  CashIcon,
  BeneficiariesIcon,
  PhonePortraitOutlineIcon as TerminalsIcon,
  PersonIcon as StaffIcon,
  LocationIcon,
  QRcodeIcon,
  PayrollIcon,
  ShieldCheckmarkIcon as KycIcon,
  // GlobeIcon as InternetSalesIcon,
  SettingsIcon as ProfileIcon,
  LogoWithTextImg,
} from '~/img';
import './styles.less';
import {
  employees,
  terminals,
  paymentDetails,
  locations,
  profile,
  transactions,
  beneficiaries,
  kyc,
  accounts,
  payrolls,
  sendMoney,
} from '~/routes';
import { usePermissions } from '~/hooks';
import { useKycCheck } from '~/hooks/kyc';
import theme from '~/theme';
import { WalletOutlined } from '@ant-design/icons';
import useIsSendMoneyAvailable from '~/hooks/useIsSendMoneyAvailable';

const Sider: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const transactionsAccess = usePermissions('business.transactions.list');
  const terminalAccess = usePermissions('business.terminal.list');
  const employeeAccess = usePermissions('business.employee.list');
  const locationAccess = usePermissions('business.location.list');
  const paymentDetailsAccess = usePermissions('business.paymentDetails.list');
  const payrollsAccess = usePermissions('business.payrolls.list');
  const profileAccess = usePermissions('business.profile.view');
  const profileUpdate = usePermissions('business.profile.update');
  const beneficiariesAccess = usePermissions('business.beneficiary.view');
  const kycCheck = useKycCheck();
  const accountsAccess = usePermissions('business.accounts.view');
  const selectedKeys = [location.pathname.split('/').slice(0, 2).join('/')];
  const hasSendMoneyFeature = useIsSendMoneyAvailable();
  const sendMoneyAccess = usePermissions('business.directTransfer.create') && hasSendMoneyFeature;

  const items: MenuProps['items'] = [
    {
      label: t('dashboard'),
      key: '/',
      icon: <DashboardIcon />,
    },
  ];
  transactionsAccess &&
    items.push({
      label: t('transactions'),
      key: `/${transactions}`,
      icon: <TransactionsIcon />,
    });
  sendMoneyAccess &&
    items.push({
      label: t('sendMoney'),
      key: `/${sendMoney}`,
      icon: <CashIcon />,
    });
  beneficiariesAccess &&
    items.push({
      label: t('beneficiaries'),
      key: `/${beneficiaries}`,
      icon: <BeneficiariesIcon />,
    });
  terminalAccess &&
    items.push({
      label: t('terminals'),
      key: `/${terminals}`,
      icon: <TerminalsIcon />,
    });
  employeeAccess &&
    items.push({
      label: t('team'),
      key: `/${employees}`,
      icon: <StaffIcon />,
    });
  locationAccess &&
    items.push({
      label: t('locations'),
      key: `/${locations}`,
      icon: <LocationIcon />,
    });
  paymentDetailsAccess &&
    items.push({
      label: t('qrCodes'),
      key: `/${paymentDetails}`,
      icon: <QRcodeIcon />,
    });
  payrollsAccess &&
    items.push({
      label: t('payrolls'),
      key: `/${payrolls}`,
      icon: <PayrollIcon />,
    });
  accountsAccess &&
    items.push({
      label: t('accounts'),
      key: `/${accounts}`,
      icon: <WalletOutlined />,
    });
  profileAccess &&
    items.push({
      label: t('profile'),
      key: `/${profile}`,
      icon: <ProfileIcon />,
    });
  if (profileAccess && profileUpdate && kycCheck.ready && !kycCheck.isCompleted) {
    items.push({
      label: t('completeProfile'),
      key: `/${kyc}`,
      icon: (
        <Badge dot={!selectedKeys.includes(`/${kyc}`) && !kycCheck.isPending}>
          <KycIcon />
        </Badge>
      ),
    });
  }

  return (
    <Layout.Sider className="Sider" theme="light">
      <div className="Sider-BusinessName">
        <LogoWithTextImg />
      </div>
      <Menu
        onClick={({ key }) => history.push(key.toString())}
        selectedKeys={selectedKeys}
        mode="inline"
        className="Sider-Menu"
        theme={theme.siderTheme as any}
        items={items}
      />
    </Layout.Sider>
  );
};

export default Sider;
